import { isNumeric } from '~/modules/helpers/math';

import {
    formatAsUserLongDate,
    formatAsUserLongDateTime
} from '~/modules/helpers/date';

import types from './customFieldTypes';

import {
    md5RegExp,
    md6RegExp,
    sha1RegExp,
    sha224RegExp,
    sha256RegExp,
    sha384RegExp,
    sha512RegExp,
    ipv4RegExp,
    urlAddressRegExp,
    getObjectValidator
} from './validation';

export function getValidationSchema(fields, raw = false) {
    const validationObject = getObjectValidator();
    const schema = {};
    if (fields) {
        for (const key of Object.keys(fields)) {
            const field = fields[key];
            if (field) {
                // If field has explicit validation defined, use it
                if (field.validation) {
                    schema[field.internalName] = field.validation;
                    continue;
                }
                
                const name = field.internalName;
                const label = field.name ?? field.label ?? '';
                const isRequired = field.required ?? field.isRequired ?? false;
                const $labelIsArequiredField = `${label} is a required field`;
                const $other = '$other';
                switch (field?.validator || field?.type) {
                    case types.TEXT:
                    case types.USER:
                    case types.TEXTAREA:
                    case 'upload':
                    case 'file_detonate':
                    case 'fileDetonate':
                        {
                            const e = validationObject.string();
                            schema[name] = isRequired
                                ? e.required($labelIsArequiredField)
                                : e.nullable();
                        }
                        break;
                    //#
                    case types.URL:
                    case 'url':
                        {
                            const e = validationObject
                                .string()
                                .requiredWhenDefined(
                                    `${label} must be a valid URL`,
                                    'url'
                                );
                            schema[name] = isRequired
                                ? e.required($labelIsArequiredField)
                                : e.nullable();
                        }
                        break;
                    //#
                    case types.EMAIL:
                    case 'e-mail':
                        {
                            let e = validationObject
                                .string()
                                .email(`${label} must be a valid email`);
                            if (isRequired) {
                                e = e.required($labelIsArequiredField);
                            }
                            schema[name] = e.nullable();
                        }
                        break;
                    //#
                    case types.NUMERIC:
                    case 'number':
                        {
                            const e = validationObject.number();
                            schema[name] = (
                                isRequired
                                    ? e.required($labelIsArequiredField)
                                    : e.nullable()
                            )
                                .typeError(`${label} value is not a number`)
                                .when($other, (other, schema) =>
                                    'integer' === field.numberType
                                        ? schema.integer(
                                              `${label} must be an integer`
                                          )
                                        : schema
                                )
                                .when($other, (other, schema) => {
                                    const minimum =
                                        field.min ||
                                        field.data_type_db?.minimum;
                                    return isNumeric(minimum)
                                        ? schema.min(
                                              parseInt(minimum),
                                              `${label} must be greater than or equal to ${minimum}`
                                          )
                                        : schema;
                                })
                                .when($other, (other, schema) => {
                                    const maximum =
                                        field.max ||
                                        field.data_type_db?.maximum;
                                    return isNumeric(maximum)
                                        ? schema.max(
                                              parseInt(maximum),
                                              `${label} must be less than or equal to ${maximum}`
                                          )
                                        : schema;
                                });
                        }
                        break;
                    //#
                    case 'integer':
                        schema[name] = validationObject
                            .string()
                            .when('$other', (other, schema) => {
                                if (isRequired) {
                                    return schema.required(
                                        $labelIsArequiredField
                                    );
                                }
                                return schema;
                            })
                            .requiredWhenDefined(
                                label + ' value is not a valid integer',
                                'integer'
                            );
                        break;
                    case types.TAGS:
                        if (isRequired) {
                            schema[name] = validationObject
                                .array()
                                .min(1, $labelIsArequiredField)
                                .nullable();
                        }
                        break;
                    //#
                    case types.LIST:
                    case types.TIMEZONE:
                    case types.TIMEINTERVAL:
                    case types.DYNAMIC:
                        {
                            let e = validationObject.array();
                            if (isRequired) {
                                e = e.min(1, $labelIsArequiredField);
                            }
                            schema[name] = e.nullable();
                        }
                        break;
                    //#
                    case types.MULTILIST:
                        if (isRequired) {
                            schema[name] = validationObject
                                .array()
                                .min(1, $labelIsArequiredField);
                        }
                        break;
                    //#
                    case types.CHECKBOX:
                        if (isRequired) {
                            schema[name] = validationObject
                                .boolean()
                                .defined()
                                .oneOf([true], $labelIsArequiredField)
                                .nullable(true);
                        }
                        break;
                    //#
                    case types.HASH:
                        schema[name] = validationObject
                            .string()
                            .when($other, (other, schema) => {
                                if (isRequired) {
                                    schema.required($labelIsArequiredField);
                                } else if (field.value) {
                                    const type = field.hashType;
                                    if (type) {
                                        const fn = re =>
                                            schema.matches(
                                                re,
                                                `Not a valid ${type.toUpperCase()}`
                                            );
                                        switch (field.hashType) {
                                            case 'md5':
                                                return fn(md5RegExp);
                                            case 'md6':
                                                return fn(md6RegExp);
                                            case 'sha1':
                                                return fn(sha1RegExp);
                                            case 'sha224':
                                                return fn(sha224RegExp);
                                            case 'sha256':
                                                return fn(sha256RegExp);
                                            case 'sha384':
                                                return fn(sha384RegExp);
                                            case 'sha512':
                                                return fn(sha512RegExp);
                                        }
                                    }
                                    // mm: if hash type is not specified check if is a generic hash
                                    return schema.hash('Not a valid hash');
                                }
                                return schema;
                            });
                        break;
                    //#
                    case types.DATE:
                    case types.TIME:
                        schema[name] = validationObject
                            .date()
                            .format()
                            .when($other, (other, schema) =>
                                isRequired
                                    ? schema.required($labelIsArequiredField)
                                    : schema
                            )
                            .when($other, (other, schema) => {
                                const min = field.min;
                                if (min) {
                                    const found = fields.find(
                                        e => min === e.internalName
                                    );
                                    const { required, value, name, type } =
                                        found || {};
                                    const fn = str =>
                                        schema.min(
                                            validationObject.ref(min),
                                            str
                                        );
                                    const fieldValue = field.value;
                                    if (required && !!value && fieldValue) {
                                        if (fieldValue < value) {
                                            return fn(
                                                `${label} must be greater than ${(types.TIME !==
                                                    type
                                                    ? formatAsUserLongDate
                                                    : formatAsUserLongDateTime)(
                                                    value
                                                )}${
                                                    name
                                                        ? `\u0020(${name})`
                                                        : ''
                                                }`
                                            );
                                        }
                                    } else {
                                        return fn(
                                            `${label} min value is ${name} required value`
                                        );
                                    }
                                }
                            });
                        break;
                    //#
                    case types.IP:
                    case 'ip':
                    case 'ipaddress':
                        schema[name] = validationObject
                            .string()
                            .nullable()
                            .when($other, (other, schema) =>
                                isRequired
                                    ? schema.required($labelIsArequiredField)
                                    : schema
                            )
                            .when($other, (other, schema) =>
                                schema.matches(
                                    ipv4RegExp,
                                    `${label} is not a valid IP`
                                )
                            );
                        break;
                    //#
                    case 'ip_domain':
                    case types.IPDOMAIN:
                        schema[name] = validationObject
                            .string()
                            .when($other, (other, schema) =>
                                isRequired
                                    ? schema.required($labelIsArequiredField)
                                    : schema
                            )
                            .ipOrDomain('Nor an IP neither a domain');
                        break;
                    //#
                    case 'domain':
                    case types.DOMAIN:
                        schema[name] = validationObject
                            .string()
                            .when('$other', (other, schema) => {
                                if (isRequired) {
                                    return schema.required(
                                        $labelIsArequiredField
                                    );
                                }
                                return schema;
                            })
                            .requiredWhenDefined(
                                label + ' is not a valid domain',
                                'domain'
                            );
                        break;
                    //#
                    case 'port':
                        schema[name] = validationObject
                            .string()
                            .when('$other', (other, schema) => {
                                if (isRequired) {
                                    return schema.required(
                                        $labelIsArequiredField
                                    );
                                }
                                return schema;
                            })
                            .requiredWhenDefined(
                                label + ' is not a valid port',
                                'port'
                            );
                        break;
                }
            }
        }
    }
    return raw ? schema : validationObject.object().shape(schema);
}
